import { useEffect, useState, useCallback } from 'react'
import { Search, RotateCcw, Undo2 } from 'lucide-react'
import api from '../api/client'
import Pagination from '../components/Pagination'
import { confirmModal, successModal, errorModal } from '../components/ui'

const money = (v) => 'R$ ' + Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })
const dt = (s) => (s ? new Date(s).toLocaleDateString('pt-BR') : '—')

export default function FaturasPagas() {
  const [rows, setRows] = useState([])
  const [page, setPage] = useState(1)
  const [size, setSize] = useState(20)
  const [meta, setMeta] = useState({ total: 0, pages: 1 })
  const [search, setSearch] = useState('')
  const [termo, setTermo] = useState('')

  const load = useCallback((p = 1, sz = size, q = termo) => {
    const qs = q ? `&search=${encodeURIComponent(q)}` : ''
    api.get(`/faturas/pagas?page=${p}&limit=${sz}${qs}`).then((r) => {
      setRows(r.data.data || []); setMeta({ total: r.data.total || 0, pages: r.data.pages || 1 }); setPage(p)
    }).catch(() => {})
  }, [size, termo])

  useEffect(() => { load(1) }, []) // eslint-disable-line

  async function estornar(f) {
    if (!(await confirmModal(`Estornar a fatura de ${f.cliente_nome || '—'} (${f.competencia})? As comissões geradas serão revertidas.`, { title: 'Estorno / Chargeback', danger: true, okLabel: 'Estornar' }))) return
    try { const r = await api.post(`/faturas/${f.id}/estornar`); await successModal(`Estornado: ${money(r.data.revertido)} revertidos (${r.data.comissoes_estornadas} comissões).`, { title: 'Estornado' }); load(page) }
    catch (e) { errorModal(e?.response?.data?.detail || 'Erro ao estornar') }
  }
  async function reprocessar(f) {
    if (!(await confirmModal(`Reprocessar PV e comissões da fatura de ${f.cliente_nome || '—'} (${f.competencia})?`, { title: 'Reprocessar', okLabel: 'Reprocessar' }))) return
    try { const r = await api.post(`/faturas/${f.id}/reprocessar`); await successModal(`Reprocessado: PV ${r.data.pv} · comissão ${money(r.data.comissao)}.`, { title: 'Reprocessado' }); load(page) }
    catch (e) { errorModal(e?.response?.data?.detail || 'Erro ao reprocessar') }
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-heading font-bold text-gray-900">Faturas Pagas</h1>
        <p className="text-sm text-gray-500 mt-0.5">Histórico de pagamentos confirmados — PV e comissões gerados. Rastreabilidade cliente → parceiro → licenciado.</p>
      </div>

      <div className="flex items-center gap-2 max-w-md">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (setTermo(search), load(1, size, search))}
            placeholder="Buscar cliente, CPF ou nº instalação..."
            className="w-full border border-gray-200 rounded-xl pl-9 pr-3 py-2 text-sm outline-none focus:border-accent-400" />
        </div>
        <button onClick={() => { setTermo(search); load(1, size, search) }} className="rounded-xl bg-gradient-to-r from-primary-500 to-accent-500 text-white px-4 py-2 text-sm font-semibold">Buscar</button>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-x-auto">
        <table className="w-full text-sm whitespace-nowrap">
          <thead className="bg-gradient-to-r from-primary-50 to-accent-50 text-gray-600 text-left text-xs uppercase tracking-wider">
            <tr>
              <th className="px-3 py-3">Cliente</th><th className="px-3 py-3">CPF/CNPJ</th>
              <th className="px-3 py-3">Licenciado</th><th className="px-3 py-3">Parceiro</th>
              <th className="px-3 py-3">Serviço</th><th className="px-3 py-3">Instalação</th>
              <th className="px-3 py-3">Ref.</th><th className="px-3 py-3 text-right">Valor</th>
              <th className="px-3 py-3 text-right">kWh</th><th className="px-3 py-3 text-right">PV</th>
              <th className="px-3 py-3 text-right">Comissão</th><th className="px-3 py-3">Vencimento</th>
              <th className="px-3 py-3">Pagamento</th><th className="px-3 py-3 text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((f) => (
              <tr key={f.id} className="border-t border-gray-100 hover:bg-primary-50/30">
                <td className="px-3 py-2.5 font-medium text-gray-900">{f.cliente_nome || '—'}</td>
                <td className="px-3 py-2.5 text-gray-500">{f.cliente_cpf || '—'}</td>
                <td className="px-3 py-2.5">{f.licenciado_nome || '—'}</td>
                <td className="px-3 py-2.5">{f.parceiro_nome || '—'}</td>
                <td className="px-3 py-2.5">{f.oferta_nome || '—'}</td>
                <td className="px-3 py-2.5 text-gray-500">{f.numero_instalacao || '—'}</td>
                <td className="px-3 py-2.5">{f.competencia}</td>
                <td className="px-3 py-2.5 text-right font-medium">{money(f.valor)}</td>
                <td className="px-3 py-2.5 text-right text-gray-500">{f.consumo_kwh != null ? Number(f.consumo_kwh).toLocaleString('pt-BR') : '—'}</td>
                <td className="px-3 py-2.5 text-right font-semibold text-blue-700">{f.pv_gerado ?? '—'}</td>
                <td className="px-3 py-2.5 text-right font-semibold text-emerald-700">{f.comissao_total != null ? money(f.comissao_total) : '—'}</td>
                <td className="px-3 py-2.5 text-gray-500">{dt(f.vencimento)}</td>
                <td className="px-3 py-2.5 text-gray-500">{dt(f.pago_em)}</td>
                <td className="px-3 py-2.5">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => reprocessar(f)} title="Reprocessar" className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50"><RotateCcw size={15} /></button>
                    <button onClick={() => estornar(f)} title="Estornar" className="p-1.5 rounded-lg text-gray-400 hover:text-rose-600 hover:bg-rose-50"><Undo2 size={15} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={14} className="px-4 py-10 text-center text-gray-400">Nenhuma fatura paga.</td></tr>}
          </tbody>
        </table>
      </div>
      <Pagination page={page} pages={meta.pages} total={meta.total} pageSize={size} onPage={load} onPageSize={(n) => { setSize(n); load(1, n) }} />
    </div>
  )
}
